import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Archive, Folder, FolderOpen, Pencil, Save, X, RefreshCcw, Search, ChevronDown, ChevronRight, Clock, Users2, Trash2, Merge, Plus, Lock, Unlock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

/** Projects page — Client → Project directory with hours rollup.
 *  Answers the user's question: "how many hours across every task under this project?"
 *  Renders a two-level list (client group → project cards) with inline rename/archive
 *  affordances for managers. Non-managers can browse but not rename. */
export default function Projects() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || !!user?.is_super_admin || !!user?.is_admin;

  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]); // canonical client roster
  const [hoursData, setHoursData] = useState({ rows: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [openClients, setOpenClients] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  // Rename-client inline state
  const [renamingClient, setRenamingClient] = useState(null); // {oldName, id?}
  const [renameClientValue, setRenameClientValue] = useState("");

  // Merge-client modal state
  const [mergeSource, setMergeSource] = useState(null); // client name being merged FROM
  const [mergeTarget, setMergeTarget] = useState("");    // client name to merge INTO
  const [mergeBusy, setMergeBusy] = useState(false);

  // New-client inline creator
  const [addingClient, setAddingClient] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  // Expanded state for the "show tasks under project" reveal.
  const [expandedProjects, setExpandedProjects] = useState({});

  // ---- Drag-and-drop project merge (same client only) ----
  const [dragProjectId, setDragProjectId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [mergeConfirm, setMergeConfirm] = useState(null); // {from, into}
  const [projMerging, setProjMerging] = useState(false);
  const runProjectMerge = async () => {
    if (!mergeConfirm) return;
    setProjMerging(true);
    try {
      const { data } = await api.post("/projects/merge", { from_id: mergeConfirm.from.id, into_id: mergeConfirm.into.id });
      toast.success(`Merged "${data.from_name}" → "${data.into_name}" · ${data.tasks_moved} task(s) moved`);
      setMergeConfirm(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Merge failed");
    } finally { setProjMerging(false); }
  };

  // ---- Access-control dialog state (Super Admin + Admins only) ----
  const canManageAccess = !!user?.is_super_admin || !!user?.is_admin;
  const [accessProject, setAccessProject] = useState(null);
  const [accessUsers, setAccessUsers] = useState([]);   // ids
  const [accessTeams, setAccessTeams] = useState([]);   // team names
  const [accessSaving, setAccessSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const openAccess = (p) => {
    setAccessProject(p);
    setAccessUsers(Array.isArray(p.allowed_user_ids) ? [...p.allowed_user_ids] : []);
    setAccessTeams(Array.isArray(p.allowed_teams) ? [...p.allowed_teams] : []);
  };
  const closeAccess = () => setAccessProject(null);
  const toggleAccessUser = (uid) =>
    setAccessUsers((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  const toggleAccessTeam = (t) =>
    setAccessTeams((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const saveAccess = async () => {
    if (!accessProject) return;
    setAccessSaving(true);
    try {
      const { data: updated } = await api.patch(`/projects/${accessProject.id}`, {
        allowed_user_ids: accessUsers,
        allowed_teams: accessTeams,
      });
      toast.success(updated.restricted ? "Access restricted" : "Access opened to everyone");
      setAccessProject(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update access");
    } finally {
      setAccessSaving(false);
    }
  };
  const clearAccess = async () => {
    if (!accessProject) return;
    setAccessSaving(true);
    try {
      await api.patch(`/projects/${accessProject.id}`, { allowed_user_ids: [], allowed_teams: [] });
      toast.success("Access opened to everyone");
      setAccessProject(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to clear access");
    } finally {
      setAccessSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Pull the full project list + the hours-by-project rollup for the last 6
      // months. Users can zoom out later if they need it — a wider range costs
      // very little on the server.
      const to = new Date();
      const from = new Date(); from.setMonth(from.getMonth() - 6);
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const [proj, hrs, cli] = await Promise.all([
        api.get("/projects", { params: { include_archived: showArchived } }),
        api.get("/reports/hours-by-project", { params: { date_from: iso(from), date_to: iso(to) } }),
        api.get("/clients", { params: { include_archived: showArchived } }),
      ]);
      setProjects(proj.data || []);
      setHoursData(hrs.data || { rows: [] });
      setClients(cli.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showArchived]);

  // Pull users + team roster once — needed by the access dialog. Admins only.
  useEffect(() => {
    if (!canManageAccess) return;
    Promise.all([
      api.get("/users"),
      api.get("/teams"),
    ]).then(([uRes, tRes]) => {
      setAllUsers(uRes.data || []);
      setAllTeams((tRes.data?.teams) || []);
    }).catch(() => { /* silent — dialog will simply show an empty list */ });
  }, [canManageAccess]);

  // Client → Projects tree. Merge the hours rollup by project id (or by
  // "unspecified" client bucket for tasks with no project set).
  const tree = useMemo(() => {
    const hoursByProjectId = new Map();
    const unspecByClient = new Map();
    for (const r of (hoursData.rows || [])) {
      if (r.project_id) hoursByProjectId.set(r.project_id, r);
      else if (r.client) unspecByClient.set(r.client, r);
    }
    // Canonical client → id lookup (case-insensitive) so the header can offer
    // rename/delete/merge affordances only when a canonical row exists.
    const clientRowByName = new Map();
    for (const c of clients) clientRowByName.set(c.name.toLowerCase(), c);
    const byClient = new Map();
    for (const p of projects) {
      if (!byClient.has(p.client)) byClient.set(p.client, []);
      const summary = hoursByProjectId.get(p.id) || { total_seconds: 0, total_hours: 0, total_days: 0, task_count: 0 };
      byClient.get(p.client).push({ ...p, ...summary });
    }
    // Include clients that only have "unspecified" hour buckets OR canonical rows
    // with zero projects (so the user can rename/delete them).
    for (const [clientName] of unspecByClient) {
      if (!byClient.has(clientName)) byClient.set(clientName, []);
    }
    for (const c of clients) {
      if (!byClient.has(c.name)) byClient.set(c.name, []);
    }
    return Array.from(byClient.entries())
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map(([client, list]) => ({
        client,
        clientRow: clientRowByName.get(client.toLowerCase()) || null,
        projects: [...list].sort((a, b) => (b.total_seconds || 0) - (a.total_seconds || 0)),
        unspecified: unspecByClient.get(client) || null,
        clientTotalSeconds: list.reduce((s, x) => s + (x.total_seconds || 0), 0) + ((unspecByClient.get(client)?.total_seconds) || 0),
      }));
  }, [projects, hoursData, clients]);

  const filteredTree = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return tree;
    return tree
      .map((g) => ({
        ...g,
        projects: g.projects.filter((p) =>
          g.client.toLowerCase().includes(needle) || p.name.toLowerCase().includes(needle)
        ),
      }))
      .filter((g) => g.client.toLowerCase().includes(needle) || g.projects.length > 0);
  }, [tree, q]);

  const fmtHours = (s) => {
    if (!s) return "0 h";
    return `${(s / 3600).toFixed(2)} h`;
  };
  const fmtDays = (d) => (d ? (d === 0.5 ? "½ day" : d === 1 ? "1 day" : `${d} days`) : "—");
  const daysFromSecs = (s) => (s ? Math.round((s / 3600) / 4) * 0.5 : 0);

  const saveRename = async (p) => {
    if (!editName.trim() || editName === p.name) { setEditingId(null); return; }
    try {
      await api.patch(`/projects/${p.id}`, { name: editName.trim() });
      toast.success("Project renamed");
      setEditingId(null); setEditName("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rename failed");
    }
  };

  const toggleArchive = async (p) => {
    try {
      await api.patch(`/projects/${p.id}`, { archived: !p.archived });
      toast.success(p.archived ? "Project un-archived" : "Project archived");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Archive failed");
    }
  };

  const deleteProject = async (p) => {
    // Non-destructive first attempt (409 if tasks are attached). If the user
    // confirms, retry with force=true which un-tags the linked tasks (project
    // becomes "Unspecified" on each) but never deletes the task itself.
    const tryDelete = async (force) => {
      await api.delete(`/projects/${p.id}${force ? "?force=true" : ""}`);
    };
    try {
      await tryDelete(false);
      toast.success(`Deleted "${p.name}"`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 409 && window.confirm(`${detail}\n\nRemove the project anyway? Tasks will keep their history but become "Unspecified".`)) {
        try {
          await tryDelete(true);
          toast.success(`Deleted "${p.name}" — attached tasks now Unspecified`);
          await load();
        } catch (err2) {
          toast.error(formatApiError(err2.response?.data?.detail) || "Delete failed");
        }
      } else if (err?.response?.status !== 409) {
        toast.error(formatApiError(detail) || "Delete failed");
      }
    }
  };

  // ---------------- Client-level actions ----------------

  const createClient = async () => {
    const name = newClientName.trim();
    if (!name) { toast.error("Enter a client name"); return; }
    try {
      const { data } = await api.post("/clients", { name });
      toast.success(data.reused ? `"${name}" already existed — highlighted` : `Created "${name}"`);
      setNewClientName(""); setAddingClient(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
    }
  };

  const saveClientRename = async (row) => {
    const nn = renameClientValue.trim();
    if (!nn || nn === row.oldName) { setRenamingClient(null); return; }
    try {
      if (row.id) {
        // Canonical row → PATCH cascades to projects+tasks server-side.
        await api.patch(`/clients/${row.id}`, { name: nn });
      } else {
        // Legacy (no canonical row): use the merge endpoint which does the
        // exact same rewrite of projects+tasks without needing an id.
        await api.post("/clients/merge", { from_name: row.oldName, into_name: nn });
      }
      toast.success(`Renamed "${row.oldName}" → "${nn}"`);
      setRenamingClient(null); setRenameClientValue("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rename failed");
    }
  };

  const deleteClient = async (g) => {
    if (!g.clientRow) {
      // Legacy client — nothing to delete server-side, but if there are tasks
      // referencing this name they'd re-appear on next fetch. Warn the user.
      toast.error("This client has no canonical row — merge it into another client instead.");
      return;
    }
    const tryDelete = async (force) => api.delete(`/clients/${g.clientRow.id}${force ? "?force=true" : ""}`);
    try {
      await tryDelete(false);
      toast.success(`Deleted "${g.client}"`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 409 && window.confirm(`${detail}\n\nCascade-delete every project under this client? Tasks will stay but their project becomes "Unspecified".`)) {
        try {
          await tryDelete(true);
          toast.success(`Deleted "${g.client}" and its projects`);
          await load();
        } catch (err2) {
          toast.error(formatApiError(err2.response?.data?.detail) || "Delete failed");
        }
      } else if (err?.response?.status !== 409) {
        toast.error(formatApiError(detail) || "Delete failed");
      }
    }
  };

  const runMerge = async () => {
    if (!mergeSource || !mergeTarget || mergeSource.toLowerCase() === mergeTarget.toLowerCase()) {
      toast.error("Pick a different destination client");
      return;
    }
    setMergeBusy(true);
    try {
      const { data } = await api.post("/clients/merge", { from_name: mergeSource, into_name: mergeTarget });
      toast.success(`Merged "${mergeSource}" into "${mergeTarget}" · ${data.projects_updated} project(s) & ${data.tasks_updated} task(s) reassigned`);
      setMergeSource(null); setMergeTarget("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Merge failed");
    } finally {
      setMergeBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-testid="projects-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-white/40">Projects</div>
          <h1 className="font-display text-3xl tracking-tight">Client &amp; project directory</h1>
          <p className="text-white/50 text-sm mt-1 max-w-2xl">
            Every task should roll up under a Project so we can see the real total hours spent on a piece of work — even when multiple editors or repeated tasks are involved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" size={14} />
            <Input
              data-testid="projects-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search client or project…"
              className="pl-7 bg-black/40 border-white/10 text-sm w-64"
            />
          </div>
          <Button
            variant="outline"
            data-testid="projects-toggle-archived"
            onClick={() => setShowArchived((v) => !v)}
            className={`border-white/15 text-xs ${showArchived ? "text-amber-300 border-amber-500/50" : "text-white/60"}`}
          >
            <Archive size={14} className="mr-1.5" /> {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          {isManager && (
            <Button
              data-testid="projects-add-client-btn"
              onClick={() => setAddingClient(true)}
              className="bg-[#E11D2E] hover:bg-[#c11024] text-xs"
            >
              <Plus size={14} className="mr-1.5" /> New Client
            </Button>
          )}
          <Button
            variant="outline"
            data-testid="projects-refresh"
            onClick={load}
            className="border-white/15 text-white/60"
          >
            <RefreshCcw size={14} />
          </Button>
        </div>
      </div>

      {addingClient && (
        <div className="glass rounded-xl p-3 flex items-center gap-2" data-testid="projects-new-client-row">
          <Input
            autoFocus
            data-testid="projects-new-client-input"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); createClient(); }
              if (e.key === "Escape") { setAddingClient(false); setNewClientName(""); }
            }}
            placeholder="New client name (e.g. Bericap)"
            className="bg-black/40 border-white/10 flex-1"
          />
          <Button
            data-testid="projects-new-client-save"
            onClick={createClient}
            className="bg-[#E11D2E] hover:bg-[#c11024]"
          >Add</Button>
          <Button
            variant="outline"
            onClick={() => { setAddingClient(false); setNewClientName(""); }}
            className="border-white/15"
          >Cancel</Button>
        </div>
      )}

      {loading ? (
        <div className="text-white/40 text-sm">Loading projects…</div>
      ) : filteredTree.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <Folder size={32} className="mx-auto text-white/30 mb-3" />
          <div className="text-white/70 font-medium">No projects yet.</div>
          <div className="text-white/40 text-xs mt-1">Create your first project from the task-create dialog by picking a client and then choosing &quot;+ New&quot; under Project.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTree.map((g) => {
            const isOpen = openClients[g.client] !== false; // default expanded
            return (
              <div key={g.client} className="glass rounded-xl overflow-hidden" data-testid={`projects-client-${g.client.replace(/\W+/g, "-")}`}>
                <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition">
                  <button
                    type="button"
                    onClick={() => setOpenClients((s) => ({ ...s, [g.client]: !isOpen }))}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    data-testid={`projects-client-toggle-${g.client.replace(/\W+/g, "-")}`}
                  >
                    {isOpen ? <ChevronDown size={16} className="text-white/50" /> : <ChevronRight size={16} className="text-white/50" />}
                    <FolderOpen size={16} className="text-[#E11D2E]" />
                    {renamingClient?.oldName === g.client ? (
                      <Input
                        autoFocus
                        data-testid={`projects-rename-client-input-${g.client.replace(/\W+/g, "-")}`}
                        value={renameClientValue}
                        onChange={(e) => setRenameClientValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") { e.preventDefault(); saveClientRename(renamingClient); }
                          if (e.key === "Escape") { setRenamingClient(null); setRenameClientValue(""); }
                        }}
                        className="bg-black/40 border-white/10 h-8 max-w-xs"
                      />
                    ) : (
                      <span className="font-display text-base tracking-tight truncate">{g.client}</span>
                    )}
                    <span className="text-[0.7rem] uppercase tracking-widest text-white/40">{g.projects.length} project{g.projects.length === 1 ? "" : "s"}</span>
                    {!g.clientRow && g.client !== "Unspecified" && (
                      <span
                        title="Legacy client — click the pencil to formally register it"
                        className="text-[0.65rem] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1 py-0.5"
                      >legacy</span>
                    )}
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-xs text-white/60 flex items-center gap-3">
                      <span><Clock size={11} className="inline mr-1" /> {fmtHours(g.clientTotalSeconds)}</span>
                      <span className="text-sky-300">{fmtDays(daysFromSecs(g.clientTotalSeconds))}</span>
                    </div>
                    {isManager && g.client !== "Unspecified" && (
                      <div className="flex items-center gap-1">
                        {renamingClient?.oldName === g.client ? (
                          <>
                            <Button
                              size="sm"
                              data-testid={`projects-rename-client-save-${g.client.replace(/\W+/g, "-")}`}
                              onClick={() => saveClientRename(renamingClient)}
                              className="bg-[#E11D2E] hover:bg-[#c11024] h-8"
                            ><Save size={13} /></Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setRenamingClient(null); setRenameClientValue(""); }}
                              className="border-white/15 h-8"
                            ><X size={13} /></Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`projects-rename-client-btn-${g.client.replace(/\W+/g, "-")}`}
                              onClick={() => { setRenamingClient({ oldName: g.client, id: g.clientRow?.id }); setRenameClientValue(g.client); }}
                              className="text-white/40 hover:text-white h-8 w-8 p-0"
                              title="Rename client"
                            ><Pencil size={13} /></Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`projects-merge-client-btn-${g.client.replace(/\W+/g, "-")}`}
                              onClick={() => { setMergeSource(g.client); setMergeTarget(""); }}
                              className="text-white/40 hover:text-sky-300 h-8 w-8 p-0"
                              title="Merge into another client"
                            ><Merge size={13} /></Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`projects-delete-client-btn-${g.client.replace(/\W+/g, "-")}`}
                              onClick={() => deleteClient(g)}
                              className="text-white/40 hover:text-red-400 h-8 w-8 p-0"
                              title="Delete client"
                            ><Trash2 size={13} /></Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-white/5 divide-y divide-white/5">
                    {g.projects.map((p) => {
                      const isExpanded = !!expandedProjects[p.id];
                      const tasksList = Array.isArray(p.tasks) ? p.tasks : [];
                      return (
                      <div key={p.id} data-testid={`projects-project-${p.id}`}>
                      <div
                        draggable={isManager && !editingId}
                        onDragStart={() => setDragProjectId(p.id)}
                        onDragEnd={() => { setDragProjectId(null); setDropTargetId(null); }}
                        onDragOver={(e) => {
                          // Only accept drops between two different projects of the SAME client group.
                          if (!dragProjectId || dragProjectId === p.id) return;
                          const source = g.projects.find((x) => x.id === dragProjectId);
                          if (!source) return;
                          e.preventDefault();
                          setDropTargetId(p.id);
                        }}
                        onDragLeave={() => setDropTargetId((cur) => (cur === p.id ? null : cur))}
                        onDrop={(e) => {
                          e.preventDefault();
                          const source = g.projects.find((x) => x.id === dragProjectId);
                          setDropTargetId(null); setDragProjectId(null);
                          if (!source || source.id === p.id) return;
                          setMergeConfirm({ from: source, into: p });
                        }}
                        className={`px-4 py-3 flex items-center gap-3 transition ${p.archived ? "opacity-60" : ""} ${dragProjectId === p.id ? "opacity-40" : ""} ${dropTargetId === p.id ? "ring-2 ring-emerald-400/60 bg-emerald-500/[0.05]" : ""}`}
                        title={isManager ? "Drag onto another project of the same client to merge" : undefined}
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedProjects((s) => ({ ...s, [p.id]: !s[p.id] }))}
                          data-testid={`projects-project-toggle-${p.id}`}
                          className="text-white/40 hover:text-white shrink-0"
                          title={isExpanded ? "Hide tasks" : "Show tasks"}
                        >
                          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                        <Folder size={14} className="text-white/40 shrink-0" />
                        <div className="flex-1 min-w-0">
                          {editingId === p.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                data-testid={`projects-rename-input-${p.id}`}
                                autoFocus
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); saveRename(p); }
                                  if (e.key === "Escape") { setEditingId(null); setEditName(""); }
                                }}
                                className="bg-black/40 border-white/10 text-sm h-8"
                              />
                              <Button size="sm" data-testid={`projects-rename-save-${p.id}`} onClick={() => saveRename(p)} className="bg-[#E11D2E] hover:bg-[#c11024] h-8"><Save size={13} /></Button>
                              <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditName(""); }} className="border-white/15 h-8"><X size={13} /></Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-white truncate">{p.name}</span>
                              {p.archived && <span className="text-[0.65rem] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">Archived</span>}
                              {p.restricted && (
                                <span
                                  className="text-[0.65rem] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
                                  data-testid={`projects-restricted-badge-${p.id}`}
                                ><Lock size={9} /> Restricted</span>
                              )}
                            </div>
                          )}
                          <div className="text-[0.7rem] text-white/40 flex items-center gap-3 mt-0.5">
                            <span><Users2 size={10} className="inline mr-0.5" /> {p.task_count || 0} task{p.task_count === 1 ? "" : "s"}</span>
                            <span>Created {(p.created_at || "").slice(0, 10)}</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-mono text-blue-300">{fmtHours(p.total_seconds || 0)}</div>
                          <div className="text-[0.7rem] text-sky-300/80">{fmtDays(daysFromSecs(p.total_seconds || 0))}</div>
                        </div>
                        {isManager && (
                          <div className="flex items-center gap-1 shrink-0">
                            {canManageAccess && (
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`projects-access-btn-${p.id}`}
                                onClick={() => openAccess(p)}
                                className={`h-8 w-8 p-0 ${p.restricted ? "text-amber-300 hover:text-amber-200" : "text-white/40 hover:text-white"}`}
                                title={p.restricted ? "Access restricted — click to manage" : "Open access · click to restrict"}
                              >{p.restricted ? <Lock size={13} /> : <Unlock size={13} />}</Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`projects-rename-btn-${p.id}`}
                              onClick={() => { setEditingId(p.id); setEditName(p.name); }}
                              className="text-white/40 hover:text-white h-8 w-8 p-0"
                              title="Rename project"
                            ><Pencil size={13} /></Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`projects-archive-btn-${p.id}`}
                              onClick={() => toggleArchive(p)}
                              className={`h-8 w-8 p-0 ${p.archived ? "text-emerald-300 hover:text-emerald-200" : "text-white/40 hover:text-amber-300"}`}
                              title={p.archived ? "Un-archive" : "Archive"}
                            ><Archive size={13} /></Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`projects-delete-btn-${p.id}`}
                              onClick={() => deleteProject(p)}
                              className="h-8 w-8 p-0 text-white/40 hover:text-red-400"
                              title="Delete project"
                            ><Trash2 size={13} /></Button>
                          </div>
                        )}
                      </div>
                      {isExpanded && (
                        <div className="bg-black/25 border-t border-white/5 py-1.5 pl-11 pr-4" data-testid={`projects-project-tasks-${p.id}`}>
                          {tasksList.length === 0 ? (
                            <div className="text-xs text-white/40 italic py-1">No tracked hours on any task in this project yet.</div>
                          ) : (
                            tasksList.map((t) => (
                              <div key={t.id} className="flex items-center justify-between py-1 gap-2" data-testid={`projects-project-task-${t.id}`}>
                                <span className="text-xs text-white/70 truncate flex-1">{t.title}</span>
                                <span className="text-[0.65rem] uppercase tracking-widest text-white/40 shrink-0 w-20 text-right">{(t.status || "").replace("_", " ")}</span>
                                <span className="text-xs font-mono text-blue-300/80 shrink-0 w-20 text-right">{fmtHours(t.seconds || 0)}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                      </div>
                      );
                    })}
                    {g.unspecified && (
                      <div className="px-4 py-2.5 bg-black/25 text-xs text-white/50 flex items-center justify-between">
                        <span className="italic">Unspecified project — {g.unspecified.task_count} legacy task{g.unspecified.task_count === 1 ? "" : "s"} not yet tagged</span>
                        <span className="text-white/60 font-mono">{fmtHours(g.unspecified.total_seconds || 0)}</span>
                      </div>
                    )}
                    {g.projects.length === 0 && !g.unspecified && (
                      <div className="px-4 py-3 text-xs text-white/40 italic">No projects for {g.client} yet.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MERGE CLIENT DIALOG — collapse duplicate client names in one shot */}
      <Dialog open={!!mergeSource} onOpenChange={(o) => { if (!o) { setMergeSource(null); setMergeTarget(""); } }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md" data-testid="projects-merge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Merge client</DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              Every project + task currently tagged <span className="text-white/90">&quot;{mergeSource}&quot;</span> will be re-tagged to the client you pick below. The old name is removed. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="overline text-white/50">Merge into…</label>
            <Select value={mergeTarget || undefined} onValueChange={setMergeTarget}>
              <SelectTrigger className="bg-black/40 border-white/10" data-testid="projects-merge-target">
                <SelectValue placeholder="Pick destination client…" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white max-h-64">
                {tree
                  .map((g) => g.client)
                  .filter((n) => n && n !== mergeSource && n !== "Unspecified")
                  .map((n) => (
                    <SelectItem key={n} value={n} data-testid={`projects-merge-target-opt-${n.replace(/\W+/g, "-")}`}>{n}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMergeSource(null); setMergeTarget(""); }} className="border-white/15">Cancel</Button>
            <Button
              onClick={runMerge}
              disabled={!mergeTarget || mergeBusy}
              data-testid="projects-merge-confirm"
              className="bg-[#E11D2E] hover:bg-[#c11024]"
            >
              {mergeBusy ? "Merging…" : `Merge into "${mergeTarget || "?"}"`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PROJECT ACCESS DIALOG — Super Admin + Admins only. Restricts who can
          see the project AND every task under it. Empty lists = open to all. */}
      <Dialog open={!!accessProject} onOpenChange={(o) => { if (!o) closeAccess(); }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Lock size={16} /> Project access — {accessProject?.name}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              Restrict who can see this project. Anyone whose id is on the member list <em>or</em> whose team is on the team list gets access. Leave both lists empty to open the project to everyone. Super Admin + Admins always see everything for auditing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <div className="overline mb-2">Teams with access</div>
              <div className="flex flex-wrap gap-2" data-testid="projects-access-teams">
                {allTeams.map((t) => {
                  const on = accessTeams.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => toggleAccessTeam(t)}
                      data-testid={`projects-access-team-${t.replace(/\W+/g, "-")}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "bg-[#E11D2E]/15 border-[#E11D2E]/50 text-[#E11D2E]" : "border-white/15 bg-white/5 text-white/70 hover:border-white/30"}`}
                    >{on ? "✓ " : ""}{t}</button>
                  );
                })}
                {allTeams.length === 0 && <div className="text-xs text-white/40">No teams loaded.</div>}
              </div>
            </div>

            <div>
              <div className="overline mb-2 flex items-center justify-between">
                <span>Individual members with access</span>
                <span className="text-[0.7rem] text-white/40 normal-case tracking-normal">{accessUsers.length} selected</span>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-md border border-white/10 bg-black/20 divide-y divide-white/5" data-testid="projects-access-users">
                {allUsers.map((u) => {
                  const on = accessUsers.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      data-testid={`projects-access-user-${u.id}`}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03] ${on ? "bg-[#E11D2E]/5" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleAccessUser(u.id)}
                        className="accent-[#E11D2E]"
                      />
                      <span className="text-sm truncate flex-1">{u.name}</span>
                      <span className="text-[0.7rem] text-white/40 font-mono uppercase tracking-widest">{u.team}</span>
                    </label>
                  );
                })}
                {allUsers.length === 0 && <div className="text-xs text-white/40 p-3">No users loaded.</div>}
              </div>
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={clearAccess}
              disabled={accessSaving}
              data-testid="projects-access-clear-btn"
              className="border-white/15 hover:border-emerald-400/40 hover:text-emerald-300"
              title="Remove all restrictions"
            >
              <Unlock size={13} className="mr-1.5" /> Open to everyone
            </Button>
            <Button
              type="button"
              onClick={saveAccess}
              disabled={accessSaving}
              data-testid="projects-access-save-btn"
              className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-6"
            >
              {accessSaving ? "Saving…" : "Save access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drag-drop merge confirmation. Same-client guard enforced by the
          backend but we filter drop targets client-side too so users can't
          even drop across clients. */}
      <Dialog open={!!mergeConfirm} onOpenChange={(o) => { if (!o) setMergeConfirm(null); }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Merge size={16} /> Merge projects?
            </DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              This will move every task from <b className="text-white/80">{mergeConfirm?.from?.name}</b> into <b className="text-white/80">{mergeConfirm?.into?.name}</b> and then delete the source project. Access allowlists are combined so nobody loses view.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setMergeConfirm(null)}>Cancel</Button>
            <Button onClick={runProjectMerge} disabled={projMerging} className="bg-[#E11D2E] hover:bg-[#F03041]" data-testid="projects-merge-confirm-btn">
              {projMerging ? "Merging…" : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WIPE-ALL CONFIRM DIALOG — removed per user request. Backend endpoint
          POST /api/clients/wipe-all still exists as a curl-only escape hatch. */}
    </div>
  );
}
